import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Container, Row, Col, Image } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

// Components
import Section from "../../Components/Section/Section";
import SearchComponent from "./components/SearchComponent";
import OrderTable from "./components/OrderTable";
import UpdateOrderModal from "./components/UpdateOrderModal";
import SortComponent from "./components/SortComponent";
import Pagination from "./components/Pagination";

// Hooks and utilities
import { useOrderList, useOrderSearch, useOrderSort } from "./hooks";
import { useIsMobile } from "../../hooks";
import { getStyles } from "./OrderList.style";
import { STORAGE_CONSTANTS } from "../../shared/Constants/Constants";
import { base_url } from "../../Api/ApiConst";

// Types
import { BreadcrumbItem, SortOption } from "./types";

// Assets
import shopImage from "../../assets/images/default-shop.png";
import { getImageUrl } from "../../Utils/imageUtils";

// Constants
const SORT_DATA: SortOption[] = [
  {
    id: 1,
    Sorttype: "id",
    name: "Product Name (Ascending)",
    Ascending: true,
  },
  {
    id: 2,
    Sorttype: "-id",
    name: "Product Name (Descending)",
    Ascending: false,
  },
  {
    id: 3,
    Sorttype: "purchase_date",
    name: "Purchase Date (Ascending)",
    Ascending: true,
  },
  {
    id: 4,
    Sorttype: "-purchase_date",
    name: "Purchase Date (Descending)",
    Ascending: false,
  },
  {
    id: 5,
    Sorttype: "status",
    name: "Order Status (Ascending)",
    Ascending: true,
  },
  {
    id: 6,
    Sorttype: "-status",
    name: "Order Status (Descending)",
    Ascending: false,
  },
  {
    id: 7,
    Sorttype: "amount",
    name: "Total Amount (Ascending)",
    Ascending: true,
  },
  {
    id: 8,
    Sorttype: "-amount",
    name: "Total Amount (Descending)",
    Ascending: false,
  },
];

const OrderList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // UI State
  const isMobile = useIsMobile(767);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearchResult, setIsSearchResult] = useState(false);

  // User Data State
  const [userToken, setUserToken] = useState<string | null>("");
  const [storeName, setStoreName] = useState<string | null>("");
  const [storeId, setStoreId] = useState<string | null>("");
  const [storeLogo, setStoreLogo] = useState<string | null>("");

  // Modal State
  const [showUpdateOrderModal, setShowUpdateOrderModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  const [deliveryMethod, setDeliveryMethod] = useState<string | null>(null);

  // Custom Hooks
  const { orders, loading, pages, fetchOrders, setOrders, setPages } = useOrderList();
  const { searchOrders } = useOrderSearch();
  const { sortOrders } = useOrderSort();

  // Memoized values
  const styles = useMemo(() => getStyles(isMobile), [isMobile]);

  const breadcrumbs: BreadcrumbItem[] = useMemo(() => [
    { text: t("footer.home"), link: "/" },
    { text: t("orderList.orders") },
  ], [t]);

  const storeLogoUrl = useMemo(() => {
    return storeLogo !== "null" ? getImageUrl(storeLogo) : shopImage;
  }, [storeLogo]);

  // Effects
  useEffect(() => {
    setUserToken(localStorage.getItem(STORAGE_CONSTANTS.USER_TOKEN));
    setStoreName(localStorage.getItem(STORAGE_CONSTANTS.STORE_NAME));
    setStoreId(localStorage.getItem(STORAGE_CONSTANTS.STORE_ID));
    setStoreLogo(localStorage.getItem(STORAGE_CONSTANTS.STORE_LOGO));
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (userToken && !isSearchResult) {
      fetchOrders(userToken, currentPage);
    }
  }, [userToken, currentPage, fetchOrders, isSearchResult]);

  // Event Handlers
  const handleSearch = useCallback(async (searchValue: string | number) => {
    try {
      if (!searchValue) {
        setIsSearchResult(false);
        setCurrentPage(1);
        return;
      }
      setIsSearchResult(true);
      const payload = {
        product_name: searchValue,
        store_based: true,
      };
      const { orders: searchResults, count } = await searchOrders(payload, 1); // Always start search from page 1
      setOrders(searchResults);
      setCurrentPage(1);
      if (count !== undefined) {
        setPages(count);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search orders");
    }
  }, [searchOrders, currentPage]);

  const handleSort = useCallback(async (sortOption: SortOption) => {
    try {
      setIsSearchResult(true); // Prevent default useEffect from overwriting
      const { orders: sortedOrders, count } = await sortOrders(sortOption.Sorttype);
      setOrders(sortedOrders);
      setCurrentPage(1);
      if (count !== undefined) {
        setPages(count);
      }
    } catch (error) {
      console.error("Sort error:", error);
      toast.error("Failed to sort orders");
    }
  }, [sortOrders]);

  const handleOrderClick = useCallback((orderId: string) => {
    localStorage.setItem(STORAGE_CONSTANTS.ORDER_ID, orderId);
    navigate("/view-order");
  }, [navigate]);

  const handleUpdateOrderClick = useCallback((
    orderId: string,
    status: string,
    method: string
  ) => {
    setCurrentOrderId(orderId);
    setCurrentStatus(status);
    setDeliveryMethod(method);
    setShowUpdateOrderModal(true);
  }, []);

  const handleCloseUpdateModal = useCallback(() => {
    setShowUpdateOrderModal(false);
    setCurrentOrderId(null);
    setCurrentStatus(null);
    setDeliveryMethod(null);
  }, []);

  const handleOrderUpdated = useCallback(() => {
    // Refresh orders after update
    if (userToken) {
      fetchOrders(userToken, currentPage);
    }
  }, [userToken, currentPage, fetchOrders]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <>
      <Section className="orderList" style={styles.bg}>
        <Container>
          <Row>
            <Col>
              <div className="breadcrumbs">
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {crumb.link ? (
                      <Link
                        to={crumb.link}
                        style={{
                          color: index === 0 ? "var(--primary-color)" : "#BEB8B8",
                          fontFamily: "var(--primary-font-medium)",
                          fontSize: 16,
                        }}
                      >
                        {crumb.text}
                      </Link>
                    ) : (
                      <span
                        style={{
                          color: index === 0 ? "var(--primary-color)" : "#BEB8B8",
                          fontFamily: "var(--primary-font-medium)",
                          fontSize: 16,
                        }}
                      >
                        {crumb.text}
                      </span>
                    )}
                    {index !== breadcrumbs.length - 1 && (
                      <FaArrowRight className="mx-2" style={{ color: "#BEB8B8" }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </Col>
          </Row>

          <SearchComponent
            onSearch={handleSearch}
            placeholder="Search..."
            className=""
            style={styles.searchRow}
          />
        </Container>
      </Section>

      <Section className="orders" style={{ marginTop: -30 }}>
        <div style={styles.whiteWrap}>
          <Container>
            {/* Store Header */}
            <div className="d-flex align-items-center justify-content-center flex-row">
              <div style={styles.shopImageWrap} className="mx-2">
                <Image
                  src={storeLogoUrl}
                  style={styles.shopImage}
                  alt={storeName || "Store"}
                />
              </div>
              <div style={styles.shopNameWrap}>
                <h3 style={styles.shopName}>{storeName}</h3>
              </div>
            </div>

            {/* Orders Header */}
            <div
              className="d-flex align-items-center justify-content-between flex-row mt-3"
              style={{ position: "relative" }}
            >
              <div className="">
                <h3 style={styles.orderTitle}>{t("orderList.orders")}</h3>
                {/* <p className="mb-0" style={styles.orderStatus}> {t("orderList.status")} : Completed</p> */}
              </div>
              <div style={styles.normalText} className="">
                {t("orderList.noOfOrders")}: {orders.length}
              </div>
              <SortComponent
                sortData={SORT_DATA}
                onSort={handleSort}
                styles={styles}
              />
            </div>

            {/* Order Table */}
            <div style={styles.orderTableWrap}>
              <OrderTable
                orders={orders}
                loading={loading}
                styles={styles}
                onOrderClick={handleOrderClick}
                onUpdateOrder={handleUpdateOrderClick}
              />

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={pages}
                onPageChange={handlePageChange}
                isSearchResult={isSearchResult}
              />
            </div>
          </Container>
        </div>
      </Section>

      {/* Update Order Modal */}
      <UpdateOrderModal
        show={showUpdateOrderModal}
        onHide={handleCloseUpdateModal}
        orderId={currentOrderId}
        currentStatus={currentStatus}
        deliveryMethod={deliveryMethod}
        onOrderUpdated={handleOrderUpdated}
      />
    </>
  );
};

export default React.memo(OrderList);
